export type ResultStatusCode =
  | 'dnf'
  | 'dsq'
  | 'dns'
  | 'nc'
  | 'lap_down'
  | 'outside_time_limit'
  | 'not_timed';

export interface ResultStatus {
  code: ResultStatusCode;
  label: string;
  raw?: string;
}

export interface ResultStatusInput {
  timeSeconds?: number | null;
  rawTime?: unknown;
  sourceMetadata?: Record<string, unknown> | null;
  assumeNotTimed?: boolean;
}

const normalize = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();

function asString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function statusFromCode(value: unknown, raw?: string): ResultStatus | null {
  const code = asString(value)?.toLowerCase().replace(/[\s-]+/g, '_');
  switch (code) {
    case 'dnf':
    case 'abandon':
    case 'abandoned':
      return { code: 'dnf', label: 'Abandon', raw };
    case 'dsq':
    case 'dq':
    case 'disqualifie':
    case 'disqualified':
      return { code: 'dsq', label: 'Disqualifié', raw };
    case 'dns':
    case 'non_partant':
    case 'absent':
    case 'forfait':
      return { code: 'dns', label: 'Non partant', raw };
    case 'nc':
    case 'non_classe':
      return { code: 'nc', label: 'Non classé', raw };
    case 'outside_time_limit':
    case 'hors_delai':
      return { code: 'outside_time_limit', label: 'Hors délai', raw };
    case 'not_timed':
    case 'non_chronometre':
      return { code: 'not_timed', label: 'Non chronométré', raw };
    default:
      return null;
  }
}

function statusFromText(value: unknown): ResultStatus | null {
  const raw = asString(value);
  if (!raw) return null;
  const text = normalize(raw);

  if (/\b(?:DSQ|DQ|DISQUALIFIEE?|DISQUALIFICATION)\b/.test(text)) {
    return { code: 'dsq', label: 'Disqualifié', raw };
  }
  if (/\b(?:DNF|ABANDON(?:NEE?)?)\b/.test(text)) {
    return { code: 'dnf', label: 'Abandon', raw };
  }
  if (/\b(?:DNS|NON PARTANTE?|ABSENTE?|FORFAIT)\b/.test(text)) {
    return { code: 'dns', label: 'Non partant', raw };
  }
  if (/\b(?:HORS DELAI|TIME LIMIT)\b/.test(text)) {
    return { code: 'outside_time_limit', label: 'Hors délai', raw };
  }
  if (/\b(?:NON CHRONOMETREE?|PAS DE CHRONO|NOT TIMED)\b/.test(text)) {
    return { code: 'not_timed', label: 'Non chronométré', raw };
  }
  const lapMatch = text.match(/\bA\s+(\d+)\s+TOUR(?:S)?\b/);
  if (lapMatch) {
    const laps = Number(lapMatch[1]);
    return {
      code: 'lap_down',
      label: `À ${laps} tour${laps > 1 ? 's' : ''}`,
      raw,
    };
  }
  if (/\b(?:NC|NON CLASSEE?)\b/.test(text)) {
    return { code: 'nc', label: 'Non classé', raw };
  }

  return null;
}

function booleanStatus(metadata: Record<string, unknown>): ResultStatus | null {
  const runs = Array.isArray(metadata['runs'])
    ? metadata['runs'].filter(
        (run): run is Record<string, unknown> => typeof run === 'object' && run !== null,
      )
    : [];

  if (metadata['isDSQ'] === true || runs.some(run => run['isDSQ'] === true)) {
    return { code: 'dsq', label: 'Disqualifié' };
  }
  if (metadata['isDNF'] === true || runs.some(run => run['isDNF'] === true)) {
    return { code: 'dnf', label: 'Abandon' };
  }
  if (metadata['isDNS'] === true || runs.some(run => run['isDNS'] === true)) {
    return { code: 'dns', label: 'Non partant' };
  }
  return null;
}

/**
 * Resolve a missing-time reason without guessing from a bare zero/null value.
 * The only inference is an explicitly identified non-timed race supplied by
 * the caller (currently an entirely untimed Cani-Marche race).
 */
export function resolveResultStatus(input: ResultStatusInput): ResultStatus | null {
  if ((input.timeSeconds ?? 0) > 0) return null;

  const metadata = input.sourceMetadata ?? {};
  const resultStatus = metadata['resultStatus'];
  if (typeof resultStatus === 'object' && resultStatus !== null) {
    const value = resultStatus as Record<string, unknown>;
    const raw = asString(value['raw']) ?? undefined;
    const fromCode = statusFromCode(value['code'], raw);
    if (fromCode) {
      const label = asString(value['label']);
      return label ? { ...fromCode, label } : fromCode;
    }
  }

  const fromCode = statusFromCode(
    metadata['resultStatusCode'] ?? metadata['statusCode'],
    asString(metadata['rawTime']) ?? undefined,
  );
  if (fromCode) {
    const label = asString(metadata['resultStatusLabel']);
    return label ? { ...fromCode, label } : fromCode;
  }

  const fromBoolean = booleanStatus(metadata);
  if (fromBoolean) return fromBoolean;

  const candidates = [
    input.rawTime,
    metadata['rawTime'],
    metadata['timeStatus'],
    metadata['status'],
    metadata['rawText'],
  ];
  for (const candidate of candidates) {
    const status = statusFromText(candidate);
    if (status) return status;
  }

  return input.assumeNotTimed
    ? { code: 'not_timed', label: 'Non chronométré' }
    : null;
}
